/**
 * pickFile — opens a native file picker and resolves with the selected file.
 * Shared by SimpleAdapter.uploadImage (picks + uploads to Blossom) and the
 * preview sandbox's onPickFile capability (picks + hands back a blob URL).
 */

export function pickFile(accept: string, signal?: AbortSignal): Promise<File> {
  return new Promise<File | null>((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    // Some browsers only reliably show the native file dialog for inputs
    // attached to the document; keep it invisible and detach it in every
    // resolution path below (change, abort, or cancel-detection).
    input.style.display = 'none';
    document.body.appendChild(input);

    const onAbort = () => {
      input.remove();
      reject(new Error('aborted'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    input.addEventListener(
      'change',
      () => {
        signal?.removeEventListener('abort', onAbort);
        const selected = input.files?.[0] ?? null;
        input.remove();
        resolve(selected);
      },
      { once: true },
    );

    // Handle user cancelling the picker
    const onFocus = () => {
      window.removeEventListener('focus', onFocus);
      setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        if (!input.files?.length) {
          input.remove();
          resolve(null);
        }
      }, 300);
    };
    window.addEventListener('focus', onFocus, { once: true });

    input.click();
  }).then((file) => {
    if (!file) throw new Error('No file selected');
    return file;
  });
}
